import type { DbExecutor } from "../executor";
import type { EntityChange, PlanModel, PlanNode, PlanObject, RoomAnchor, Wall, WallOpening } from "../../geometry";
import type { RoomGeometry } from "../../domain/room";

/**
 * Persistence for the building geometry (Phase 7). Geometry belongs to one
 * DESIGN (scenario) per floor; loadFloor loads that design's plan.
 * Operation CHANGESETS are applied by upserting each entity state; a null
 * target archives the row (rows are never deleted — identity survives).
 * Room anchors + the derived cache live in design_room_geometry, keyed by
 * (design, room). Standard SQL only.
 */
/** One kind of object at one width, and how many of them (spec §84-4). */
export interface ObjectSizeGroup {
  definitionId: string;
  definitionName: string;
  /** Null for a RUN: its size is its length, not a width. */
  widthPu: number | null;
  count: number;
}

export class PlanRepository {
  constructor(private readonly db: DbExecutor) {}

  async loadFloor(designId: string, floorId: string): Promise<PlanModel> {
    const nodes = await this.db.select<{ id: string; floor_id: string; x: number; y: number; origin_id: string | null; archived_at: string | null }>(
      "SELECT id, floor_id, x, y, origin_id, archived_at FROM plan_nodes WHERE design_id = ? AND floor_id = ?",
      [designId, floorId],
    );
    const walls = await this.db.select<{
      id: string;
      floor_id: string;
      kind: string;
      start_node_id: string;
      end_node_id: string;
      thickness: number;
      height_inches: number | null;
      exterior_override: number | null;
      origin_id: string | null;
      notes: string;
      archived_at: string | null;
    }>(
      "SELECT id, floor_id, kind, start_node_id, end_node_id, thickness, height_inches, exterior_override, origin_id, notes, archived_at FROM walls WHERE design_id = ? AND floor_id = ?",
      [designId, floorId],
    );
    const openings =
      walls.length === 0
        ? []
        : await this.db.select<{
            id: string;
            wall_id: string;
            kind: string;
            offset_from_start: number;
            width: number;
            height: number;
            sill_height: number;
            swing: string | null;
            origin_id: string | null;
            notes: string;
            archived_at: string | null;
            window_config: string;
            window_operation: string;
          }>(
            `SELECT o.id, o.wall_id, o.kind, o.offset_from_start, o.width, o.height, o.sill_height, o.swing, o.origin_id, o.notes, o.archived_at,
                    o.window_config, o.window_operation
               FROM wall_openings o JOIN walls w ON w.id = o.wall_id WHERE w.design_id = ? AND w.floor_id = ?`,
            [designId, floorId],
          );
    const objects = await this.db.select<ObjectRow>(
      `SELECT id, floor_id, definition_id, geometry_type, x, y, end_x, end_y, rotation_x10, width, depth, height_in, mount_height_in,
              parent_object_id, detached, host_wall_id, host_offset, host_side, room_id, feature_id, config, origin_id, notes, archived_at
         FROM plan_objects WHERE design_id = ? AND floor_id = ?`,
      [designId, floorId],
    );
    const anchors = await this.db.select<{ room_id: string; floor_id: string; anchor_x: number; anchor_y: number }>(
      `SELECT g.room_id, g.floor_id, g.anchor_x, g.anchor_y FROM design_room_geometry g
         JOIN rooms r ON r.id = g.room_id WHERE g.design_id = ? AND g.floor_id = ? AND r.archived_at IS NULL`,
      [designId, floorId],
    );
    const model: PlanModel = {
      designId,
      floorId,
      nodes: new Map(
        nodes.map((n) => [
          n.id,
          { id: n.id, floorId: n.floor_id, x: Number(n.x), y: Number(n.y), originId: n.origin_id, archivedAt: n.archived_at } satisfies PlanNode,
        ]),
      ),
      walls: new Map(
        walls.map((w) => [
          w.id,
          {
            id: w.id,
            floorId: w.floor_id,
            kind: (w.kind as Wall["kind"]) ?? "wall",
            startNodeId: w.start_node_id,
            endNodeId: w.end_node_id,
            thickness: Number(w.thickness),
            heightInches: w.height_inches === null ? null : Number(w.height_inches),
            exteriorOverride: w.exterior_override === null ? null : Number(w.exterior_override) === 1,
            originId: w.origin_id,
            notes: w.notes,
            archivedAt: w.archived_at,
          } satisfies Wall,
        ]),
      ),
      openings: new Map(
        openings.map((o) => [
          o.id,
          {
            id: o.id,
            wallId: o.wall_id,
            kind: o.kind as WallOpening["kind"],
            offsetFromStart: Number(o.offset_from_start),
            width: Number(o.width),
            height: Number(o.height),
            sillHeight: Number(o.sill_height),
            swing: o.swing as WallOpening["swing"],
            windowConfig: (o.window_config || "single") as WallOpening["windowConfig"],
            windowOperation: (o.window_operation || "") as WallOpening["windowOperation"],
            originId: o.origin_id,
            notes: o.notes,
            archivedAt: o.archived_at,
          } satisfies WallOpening,
        ]),
      ),
      objects: new Map(objects.map((o) => [o.id, mapObject(o)])),
      anchors: new Map(anchors.map((a) => [a.room_id, { roomId: a.room_id, floorId: a.floor_id, x: Number(a.anchor_x), y: Number(a.anchor_y) } satisfies RoomAnchor])),
    };
    return model;
  }

  /**
   * Persist a changeset for one design. `direction` 'forward' writes `after`
   * states (do / redo); 'backward' writes `before` states (undo). Null
   * state = archive (anchors: row removed). FK order: nodes → walls →
   * openings → anchors.
   */
  async applyChanges(designId: string, changes: EntityChange[], direction: "forward" | "backward"): Promise<void> {
    const now = new Date().toISOString();
    const order: Record<EntityChange["kind"], number> = { node: 0, wall: 1, opening: 2, object: 3, anchor: 4 };
    const sorted = changes.slice().sort((a, b) => order[a.kind] - order[b.kind]);
    for (const c of sorted) {
      const state = direction === "forward" ? c.after : c.before;
      switch (c.kind) {
        case "node":
          if (state) await this.upsertNode(designId, state as PlanNode, now);
          else await this.db.execute("UPDATE plan_nodes SET archived_at = COALESCE(archived_at, ?), updated_at = ? WHERE id = ?", [now, now, c.id]);
          break;
        case "wall":
          if (state) await this.upsertWall(designId, state as Wall, now);
          else await this.db.execute("UPDATE walls SET archived_at = COALESCE(archived_at, ?), updated_at = ? WHERE id = ?", [now, now, c.id]);
          break;
        case "opening":
          if (state) await this.upsertOpening(state as WallOpening, now);
          else await this.db.execute("UPDATE wall_openings SET archived_at = COALESCE(archived_at, ?), updated_at = ? WHERE id = ?", [now, now, c.id]);
          break;
        case "object":
          if (state) await this.upsertObject(designId, state as PlanObject, now);
          else await this.db.execute("UPDATE plan_objects SET archived_at = COALESCE(archived_at, ?), updated_at = ? WHERE id = ?", [now, now, c.id]);
          break;
        case "anchor":
          if (state) await this.upsertAnchor(designId, state as RoomAnchor, now);
          else await this.db.execute("DELETE FROM design_room_geometry WHERE design_id = ? AND room_id = ?", [designId, c.id]);
          break;
      }
    }
  }

  private async upsertNode(designId: string, n: PlanNode, now: string): Promise<void> {
    const existing = await this.db.select<{ id: string }>("SELECT id FROM plan_nodes WHERE id = ?", [n.id]);
    if (existing.length === 0) {
      await this.db.execute("INSERT INTO plan_nodes (id, floor_id, design_id, x, y, origin_id, archived_at, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)", [
        n.id,
        n.floorId,
        designId,
        n.x,
        n.y,
        n.originId,
        n.archivedAt,
        now,
        now,
      ]);
    } else {
      await this.db.execute("UPDATE plan_nodes SET x = ?, y = ?, archived_at = ?, updated_at = ? WHERE id = ?", [n.x, n.y, n.archivedAt, now, n.id]);
    }
  }

  private async upsertWall(designId: string, w: Wall, now: string): Promise<void> {
    const existing = await this.db.select<{ id: string }>("SELECT id FROM walls WHERE id = ?", [w.id]);
    const ext = w.exteriorOverride === null ? null : w.exteriorOverride ? 1 : 0;
    if (existing.length === 0) {
      await this.db.execute(
        `INSERT INTO walls (id, floor_id, design_id, kind, start_node_id, end_node_id, thickness, height_inches, exterior_override, origin_id, notes, archived_at, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [w.id, w.floorId, designId, w.kind, w.startNodeId, w.endNodeId, w.thickness, w.heightInches, ext, w.originId, w.notes, w.archivedAt, now, now],
      );
    } else {
      await this.db.execute(
        `UPDATE walls SET kind = ?, start_node_id = ?, end_node_id = ?, thickness = ?, height_inches = ?, exterior_override = ?, notes = ?, archived_at = ?, updated_at = ? WHERE id = ?`,
        [w.kind, w.startNodeId, w.endNodeId, w.thickness, w.heightInches, ext, w.notes, w.archivedAt, now, w.id],
      );
    }
  }

  private async upsertOpening(o: WallOpening, now: string): Promise<void> {
    const existing = await this.db.select<{ id: string }>("SELECT id FROM wall_openings WHERE id = ?", [o.id]);
    if (existing.length === 0) {
      await this.db.execute(
        `INSERT INTO wall_openings (id, wall_id, kind, offset_from_start, width, height, sill_height, swing, origin_id, notes, archived_at, created_at, updated_at, window_config, window_operation)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [o.id, o.wallId, o.kind, o.offsetFromStart, o.width, o.height, o.sillHeight, o.swing, o.originId, o.notes, o.archivedAt, now, now, o.windowConfig, o.windowOperation],
      );
    } else {
      await this.db.execute(
        `UPDATE wall_openings SET wall_id = ?, kind = ?, offset_from_start = ?, width = ?, height = ?, sill_height = ?, swing = ?, notes = ?, archived_at = ?, updated_at = ?, window_config = ?, window_operation = ? WHERE id = ?`,
        [o.wallId, o.kind, o.offsetFromStart, o.width, o.height, o.sillHeight, o.swing, o.notes, o.archivedAt, now, o.windowConfig, o.windowOperation, o.id],
      );
    }
  }

  private async upsertObject(designId: string, o: PlanObject, now: string): Promise<void> {
    const existing = await this.db.select<{ id: string }>("SELECT id FROM plan_objects WHERE id = ?", [o.id]);
    const params = [
      o.floorId, o.definitionId, o.geometryType, o.x, o.y, o.endX, o.endY, o.rotationX10, o.width, o.depth,
      o.heightInches, o.mountHeightInches, o.parentObjectId, o.detached ? 1 : 0, o.hostWallId, o.hostOffset, o.hostSide,
      o.roomId, o.featureId, o.config, o.originId, o.notes, o.archivedAt,
    ];
    if (existing.length === 0) {
      await this.db.execute(
        `INSERT INTO plan_objects (floor_id, definition_id, geometry_type, x, y, end_x, end_y, rotation_x10, width, depth,
            height_in, mount_height_in, parent_object_id, detached, host_wall_id, host_offset, host_side, room_id, feature_id,
            config, origin_id, notes, archived_at, design_id, id, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [...params, designId, o.id, now, now],
      );
    } else {
      await this.db.execute(
        `UPDATE plan_objects SET floor_id = ?, definition_id = ?, geometry_type = ?, x = ?, y = ?, end_x = ?, end_y = ?, rotation_x10 = ?, width = ?, depth = ?,
            height_in = ?, mount_height_in = ?, parent_object_id = ?, detached = ?, host_wall_id = ?, host_offset = ?, host_side = ?, room_id = ?, feature_id = ?,
            config = ?, origin_id = ?, notes = ?, archived_at = ?, updated_at = ? WHERE id = ?`,
        [...params, now, o.id],
      );
    }
  }

  /** Active objects bound to a Feature across all floors of its design. */
  async listObjectsByIds(ids: readonly string[]): Promise<PlanObject[]> {
    if (ids.length === 0) return [];
    const rows = await this.db.select<ObjectRow & { design_id: string }>(
      `SELECT * FROM plan_objects WHERE id IN (${ids.map(() => "?").join(",")})`,
      [...ids],
    );
    return rows.map(mapObject);
  }

  /** Active objects bound to a Feature across all floors of its design. */
  async listObjectsForFeature(featureId: string): Promise<PlanObject[]> {
    const rows = await this.db.select<ObjectRow & { design_id: string }>(
      `SELECT id, floor_id, design_id, definition_id, geometry_type, x, y, end_x, end_y, rotation_x10, width, depth, height_in, mount_height_in,
              parent_object_id, detached, host_wall_id, host_offset, host_side, room_id, feature_id, origin_id, notes, archived_at
         FROM plan_objects WHERE feature_id = ? AND archived_at IS NULL`,
      [featureId],
    );
    return rows.map(mapObject);
  }

  /**
   * The SIZES behind each feature's objects, for the estimate (spec §84-4).
   *
   * Fifteen cabinets of three widths are three things to buy, not fifteen
   * lines and not one anonymous total — so the objects are counted by kind
   * AND width. A feature may hold one group (base cabinets aggregate per
   * room, so this is the usual shape) or several.
   */
  async listObjectSizesForDesign(designId: string): Promise<Map<string, ObjectSizeGroup[]>> {
    /* A WIDTH ONLY MEANS SOMETHING FOR A MODULE. A countertop or a cabinet
     * RUN is sized by its length — which is already the item's quantity in
     * linear feet — so a run reports no width rather than the "0 inches"
     * its width column happens to hold. */
    const rows = await this.db.select<{ feature_id: string; definition_id: string; display_name: string; width: number | null; n: number }>(
      `SELECT po.feature_id, po.definition_id, od.display_name,
              CASE WHEN po.geometry_type = 'point' THEN po.width ELSE NULL END AS width,
              COUNT(*) AS n
         FROM plan_objects po JOIN object_definitions od ON od.id = po.definition_id
        WHERE po.design_id = ? AND po.archived_at IS NULL AND po.feature_id IS NOT NULL
        GROUP BY po.feature_id, po.definition_id, od.display_name,
                 CASE WHEN po.geometry_type = 'point' THEN po.width ELSE NULL END
        ORDER BY po.definition_id, width DESC`,
      [designId],
    );
    const out = new Map<string, ObjectSizeGroup[]>();
    for (const r of rows) {
      const list = out.get(r.feature_id) ?? [];
      list.push({ definitionId: r.definition_id, definitionName: r.display_name, widthPu: r.width, count: r.n });
      out.set(r.feature_id, list);
    }
    return out;
  }

  /** Floors of a design that contain a given feature's bound objects (navigation). */
  async objectLocationsForFeature(featureId: string): Promise<Array<{ designId: string; floorId: string; objectId: string }>> {
    const rows = await this.db.select<{ design_id: string; floor_id: string; id: string }>(
      "SELECT design_id, floor_id, id FROM plan_objects WHERE feature_id = ? AND archived_at IS NULL",
      [featureId],
    );
    return rows.map((r) => ({ designId: r.design_id, floorId: r.floor_id, objectId: r.id }));
  }

  private async upsertAnchor(designId: string, a: RoomAnchor, now: string): Promise<void> {
    const existing = await this.db.select<{ room_id: string }>("SELECT room_id FROM design_room_geometry WHERE design_id = ? AND room_id = ?", [designId, a.roomId]);
    if (existing.length === 0) {
      await this.db.execute(
        "INSERT INTO design_room_geometry (design_id, room_id, floor_id, anchor_x, anchor_y, floor_area_x100, perimeter_x100, polygon_json, status, updated_at) VALUES (?, ?, ?, ?, ?, NULL, NULL, NULL, 'unbound', ?)",
        [designId, a.roomId, a.floorId, a.x, a.y, now],
      );
    } else {
      await this.db.execute("UPDATE design_room_geometry SET floor_id = ?, anchor_x = ?, anchor_y = ?, updated_at = ? WHERE design_id = ? AND room_id = ?", [
        a.floorId,
        a.x,
        a.y,
        now,
        designId,
        a.roomId,
      ]);
    }
  }

  // ---- Derived room geometry cache ------------------------------------------

  private static readonly GEOMETRY_SELECT = `SELECT g.design_id, g.room_id, g.floor_id, g.anchor_x, g.anchor_y, g.floor_area_x100, g.perimeter_x100, g.status, g.updated_at FROM design_room_geometry g`;

  async listRoomGeometryByDesign(designId: string): Promise<RoomGeometry[]> {
    const rows = await this.db.select<RoomGeometryRow>(`${PlanRepository.GEOMETRY_SELECT} WHERE g.design_id = ?`, [designId]);
    return rows.map(mapGeometry);
  }

  /** Which rooms are anchored in WHICH design, project-wide — the room
   * currency rule (spec §69-13) needs both halves in one read. */
  async listRoomAnchorsByProject(projectId: string): Promise<Array<{ roomId: string; designId: string }>> {
    const rows = await this.db.select<{ room_id: string; design_id: string }>(
      `SELECT g.room_id, g.design_id FROM design_room_geometry g
         JOIN designs d ON d.id = g.design_id
        WHERE d.project_id = ?`,
      [projectId],
    );
    return rows.map((row) => ({ roomId: row.room_id, designId: row.design_id }));
  }

  async listRoomGeometryByFloor(designId: string, floorId: string): Promise<RoomGeometry[]> {
    const rows = await this.db.select<RoomGeometryRow>(`${PlanRepository.GEOMETRY_SELECT} WHERE g.design_id = ? AND g.floor_id = ?`, [designId, floorId]);
    return rows.map(mapGeometry);
  }

  async getRoomGeometry(designId: string, roomId: string): Promise<RoomGeometry | null> {
    const rows = await this.db.select<RoomGeometryRow>(`${PlanRepository.GEOMETRY_SELECT} WHERE g.design_id = ? AND g.room_id = ?`, [designId, roomId]);
    return rows.length === 0 ? null : mapGeometry(rows[0]);
  }

  /** SCOPE-SOURCE geometry: the design named by projects.active_design_id. */
  async listActiveRoomGeometryByProject(projectId: string): Promise<RoomGeometry[]> {
    const rows = await this.db.select<RoomGeometryRow>(
      `${PlanRepository.GEOMETRY_SELECT} JOIN projects p ON p.active_design_id = g.design_id JOIN floors f ON f.id = g.floor_id WHERE f.project_id = ? AND p.id = ?`,
      [projectId, projectId],
    );
    return rows.map(mapGeometry);
  }

  async getActiveRoomGeometry(roomId: string): Promise<RoomGeometry | null> {
    const rows = await this.db.select<RoomGeometryRow>(
      `${PlanRepository.GEOMETRY_SELECT}
         JOIN floors f ON f.id = g.floor_id
         JOIN projects p ON p.id = f.project_id AND p.active_design_id = g.design_id
        WHERE g.room_id = ?`,
      [roomId],
    );
    return rows.length === 0 ? null : mapGeometry(rows[0]);
  }

  /** Coordinator writes derived values; the anchor itself is only changed through operations. */
  async updateRoomGeometryCache(
    designId: string,
    roomId: string,
    values: { floorAreaX100: number | null; perimeterX100: number | null; status: RoomGeometry["status"]; polygonJson: string | null },
  ): Promise<void> {
    await this.db.execute(
      "UPDATE design_room_geometry SET floor_area_x100 = ?, perimeter_x100 = ?, status = ?, polygon_json = ?, updated_at = ? WHERE design_id = ? AND room_id = ?",
      [values.floorAreaX100, values.perimeterX100, values.status, values.polygonJson, new Date().toISOString(), designId, roomId],
    );
  }

  /** Active wall counts per floor for one design (summaries). */
  async countActiveWallsByFloor(designId: string): Promise<Map<string, number>> {
    const rows = await this.db.select<{ floor_id: string; n: number }>(
      `SELECT floor_id, COUNT(*) AS n FROM walls WHERE design_id = ? AND archived_at IS NULL GROUP BY floor_id`,
      [designId],
    );
    return new Map(rows.map((r) => [r.floor_id, Number(r.n)]));
  }
}

interface ObjectRow {
  id: string;
  floor_id: string;
  definition_id: string;
  geometry_type: string;
  x: number;
  y: number;
  end_x: number | null;
  end_y: number | null;
  rotation_x10: number;
  width: number;
  depth: number;
  height_in: number | null;
  mount_height_in: number | null;
  parent_object_id: string | null;
  detached: number;
  host_wall_id: string | null;
  host_offset: number | null;
  host_side: number | null;
  room_id: string | null;
  feature_id: string | null;
  config: string | null;
  origin_id: string | null;
  notes: string;
  archived_at: string | null;
}

function mapObject(o: ObjectRow): PlanObject {
  return {
    id: o.id,
    floorId: o.floor_id,
    definitionId: o.definition_id,
    geometryType: o.geometry_type as PlanObject["geometryType"],
    x: Number(o.x),
    y: Number(o.y),
    endX: o.end_x === null ? null : Number(o.end_x),
    endY: o.end_y === null ? null : Number(o.end_y),
    rotationX10: Number(o.rotation_x10),
    width: Number(o.width),
    depth: Number(o.depth),
    heightInches: o.height_in === null ? null : Number(o.height_in),
    mountHeightInches: o.mount_height_in === null ? null : Number(o.mount_height_in),
    parentObjectId: o.parent_object_id,
    detached: Number(o.detached) === 1,
    hostWallId: o.host_wall_id,
    hostOffset: o.host_offset === null ? null : Number(o.host_offset),
    hostSide: o.host_side === null ? null : (Number(o.host_side) as 1 | -1),
    roomId: o.room_id,
    featureId: o.feature_id,
    config: o.config ?? null,
    originId: o.origin_id,
    notes: o.notes,
    archivedAt: o.archived_at,
  };
}

interface RoomGeometryRow {
  design_id: string;
  room_id: string;
  floor_id: string;
  anchor_x: number;
  anchor_y: number;
  floor_area_x100: number | null;
  perimeter_x100: number | null;
  status: string;
  updated_at: string;
}

function mapGeometry(r: RoomGeometryRow): RoomGeometry {
  return {
    designId: r.design_id,
    roomId: r.room_id,
    floorId: r.floor_id,
    anchorX: Number(r.anchor_x),
    anchorY: Number(r.anchor_y),
    floorAreaX100: r.floor_area_x100 === null ? null : Number(r.floor_area_x100),
    perimeterX100: r.perimeter_x100 === null ? null : Number(r.perimeter_x100),
    status: r.status as RoomGeometry["status"],
    updatedAt: r.updated_at,
  };
}
